import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismLoadingBar,
  PrismModal,
  PrismRadioGroup,
} from '@mylighthouse/prism-ember';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { task } from 'ember-concurrency';
import { eq } from 'ember-truth-helpers';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { PMS_VALUE_TYPES } from 'lumen/utils/central-mapping-constants';
import {
  MAPPING_TYPE_MARKET_CODE,
  MAPPING_TYPE_RATE_CODE,
} from 'lumen/utils/constants/pms-mapping-constants';

interface SettingsMappingModalsMappingTypeModalSignature {
  Args: {
    getHierarchy?: () => void;
    getMapping?: () => void;
    onCloseModal: () => void;
    usage?: 'revenue-insights' | 'central-mapping';
  };
}

export default class SettingsMappingModalsMappingTypeModal extends Component<SettingsMappingModalsMappingTypeModalSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];

  @tracked revenueInsightMappingSegmentBreakdown =
    this.hotel.revenueInsightMappingSegmentBreakdown;

  saveMappingTypeTask = task({ drop: true }, async (): Promise<void> => {
    this.hotel.revenueInsightMappingSegmentBreakdown =
      this.revenueInsightMappingSegmentBreakdown;
    await this.hotel.save();

    this.toast.success(this.intl.t('general.changes_saved.body'), {
      title: this.intl.t('general.changes_saved'),
      timeout: TOAST_TIMEOUT_LONG,
      showIcon: true,
    });

    // Refetch the hierarchy and mapping now that the mapping type is stored
    const { getHierarchy, getMapping } = this.args;
    getHierarchy?.();
    getMapping?.();

    this.args.onCloseModal();
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    return this.hotelAndMarketService.hotel;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get modalTextContent() {
    if (this.args.usage === 'central-mapping') {
      return {
        title: this.intl.t(
          'settings.central_mapping.temporary_mapping_type_settings.title',
        ),
        body: this.intl.t(
          'settings.central_mapping.temporary_mapping_type_settings.body',
        ),
        marketCodeRadioText: this.intl.t('settings.pms_mapping.market_code'),
        rateCodeRadioText: this.intl.t('general.rate_code'),
        confirmButtonText: this.intl.t('general.apply'),
      };
    }

    return {
      title: this.intl.t(
        'settings.pms_mapping.modal_change_mapping_type.heading',
      ),
      body: this.intl.t('settings.pms_mapping.modal_change_mapping_type.body'),
      marketCodeRadioText: this.intl.t(
        'settings.pms_mapping.market_segment_code',
      ),
      rateCodeRadioText: this.intl.t('revenue_insight.rate_plan'),
      confirmButtonText: this.intl.t(
        'settings.pms_mapping.modal_change_mapping_type.change_mapping',
      ),
    };
  }

  @action
  setRevenueInsightMappingSegmentBreakdown(event: Event): void {
    this.revenueInsightMappingSegmentBreakdown = (
      event.target as HTMLInputElement
    )?.value as
      | typeof PMS_VALUE_TYPES.MARKET_CODE
      | typeof PMS_VALUE_TYPES.RATE_CODE;
  }

  <template>
    <PrismModal
      @onClose={{@onCloseModal}}
      @title={{this.modalTextContent.title}}
    >
      <div class="modal--small modal--padded settings-modal__content">
        {{#if this.saveMappingTypeTask.isRunning}}
          <PrismLoadingBar />
        {{/if}}

        <p class="settings-modal__text paragraph-300--regular">
          {{this.modalTextContent.body}}
        </p>

        <PrismRadioGroup
          @isHorizontal={{true}}
          @name="mapping-type-radio-group"
          @onChange={{this.setRevenueInsightMappingSegmentBreakdown}}
          @value={{this.revenueInsightMappingSegmentBreakdown}}
          as |RadioGroup|
        >
          <RadioGroup.input @value={{MAPPING_TYPE_MARKET_CODE}}>
            {{this.modalTextContent.marketCodeRadioText}}
          </RadioGroup.input>
          <RadioGroup.input @value={{MAPPING_TYPE_RATE_CODE}}>
            {{this.modalTextContent.rateCodeRadioText}}
          </RadioGroup.input>
        </PrismRadioGroup>
      </div>

      <div class="settings-modal__footer">
        <GeneralFormFieldsCancelAndConfirm
          @cancel={{@onCloseModal}}
          @confirm={{this.saveMappingTypeTask.perform}}
          @confirmText={{this.modalTextContent.confirmButtonText}}
          @isConfirmDisabled={{eq
            this.revenueInsightMappingSegmentBreakdown
            this.hotel.revenueInsightMappingSegmentBreakdown
          }}
        />
      </div>
    </PrismModal>
  </template>
}
